/**
 * 📖 Descrições Amigáveis das Permissões
 * 
 * Converte permissões técnicas em descrições legíveis para o usuário.
 * Usado para exibir as permissões de forma clara nos modais e interfaces.
 * 
 * @author Pagmus Team
 * @date 2025-11-06
 */

// ==========================================
// 📋 DESCRIÇÕES DOS MÓDULOS
// ==========================================
export const MODULE_DESCRIPTIONS = {
  produtos: {
    title: 'Produtos',
    icon: 'mdi:package-variant',
    description: 'Gerenciamento de produtos da plataforma',
    actions: {
      view: 'Visualizar produtos existentes e seus detalhes',
      create: 'Cadastrar novos produtos na plataforma',
      edit: 'Editar informações de produtos existentes',
      delete: 'Excluir produtos da plataforma',
      manage: 'Gerenciar completamente todos os produtos'
    }
  },
  
  financeiro: {
    title: 'Financeiro',
    icon: 'mdi:cash-multiple',
    description: 'Controle financeiro e bancário',
    actions: {
      view: 'Visualizar contas bancárias, saques e responsáveis financeiros',
      manage: 'Gerenciar saques, contas bancárias e configurações financeiras',
      export: 'Exportar relatórios e extratos financeiros'
    }
  },
  
  relatorios: {
    title: 'Relatórios',
    icon: 'mdi:chart-line',
    description: 'Relatórios e análises',
    actions: {
      view: 'Visualizar todos os relatórios e indicadores da plataforma',
      export: 'Exportar relatórios em diferentes formatos (PDF, Excel, etc)',
      manage: 'Gerenciar e configurar relatórios personalizados'
    }
  },
  
  vendas: {
    title: 'Vendas',
    icon: 'mdi:cart',
    description: 'Gestão de vendas e pedidos',
    actions: {
      view: 'Visualizar todas as vendas, pedidos e status',
      manage: 'Gerenciar vendas, alterar status, processar pagamentos',
      create: 'Criar novos pedidos manualmente',
      edit: 'Editar informações de vendas existentes',
      delete: 'Excluir ou cancelar vendas'
    }
  },
  
  afiliados: {
    title: 'Afiliados',
    icon: 'mdi:account-group',
    description: 'Gerenciamento de afiliados',
    actions: {
      view: 'Visualizar afiliados, solicitações e comissões',
      manage: 'Aprovar solicitações, gerenciar comissões e links de afiliados',
      create: 'Convidar novos afiliados',
      edit: 'Editar dados e comissões de afiliados',
      delete: 'Remover afiliados da plataforma'
    }
  },
  
  usuarios: {
    title: 'Usuários e Equipe',
    icon: 'mdi:account-multiple',
    description: 'Gestão de usuários e colaboradores',
    actions: {
      view: 'Visualizar lista de usuários e membros da equipe',
      create: 'Cadastrar novos colaboradores e vendedores',
      edit: 'Editar informações e permissões de usuários',
      delete: 'Remover usuários da plataforma',
      manage: 'Gerenciar completamente usuários, permissões e grupos'
    }
  },
  
  compras: {
    title: 'Compras',
    icon: 'mdi:shopping',
    description: 'Visualização de compras realizadas',
    actions: {
      view: 'Visualizar histórico de compras próprias',
      manage: 'Gerenciar e acompanhar pedidos de compras'
    }
  }
};

// ==========================================
// 🎨 FUNÇÃO PARA GERAR DESCRIÇÃO LEGÍVEL
// ==========================================
/**
 * Converte um objeto de permissões em lista de descrições legíveis
 * @param {Object} permissions - Objeto de permissões { produtos: { view: true, ... }, ... }
 * @returns {Array} Lista de { module, title, icon, actions: [...] }
 */
export function getReadablePermissions(permissions) {
  if (!permissions || typeof permissions !== 'object') {
    return [];
  }

  const result = [];

  // Iterar sobre cada módulo
  Object.keys(permissions).forEach(moduleKey => {
    const moduleConfig = MODULE_DESCRIPTIONS[moduleKey];
    
    if (!moduleConfig) {
      console.warn(`⚠️ Módulo não encontrado no dicionário: ${moduleKey}`);
      return;
    }

    const modulePermissions = permissions[moduleKey];
    if (!modulePermissions || typeof modulePermissions !== 'object') {
      return;
    }

    // Filtrar apenas as ações ativas (true)
    const activeActions = [];
    Object.keys(modulePermissions).forEach(actionKey => {
      if (modulePermissions[actionKey] === true && moduleConfig.actions[actionKey]) {
        activeActions.push({
          key: actionKey,
          description: moduleConfig.actions[actionKey]
        });
      }
    });

    // Só adicionar o módulo se tiver pelo menos uma ação ativa
    if (activeActions.length > 0) {
      result.push({
        module: moduleKey,
        title: moduleConfig.title,
        icon: moduleConfig.icon,
        description: moduleConfig.description,
        actions: activeActions
      });
    }
  });

  return result;
}

/**
 * Conta quantas permissões estão ativas em um objeto de permissões
 * @param {Object} permissions - Objeto de permissões
 * @returns {Number} Total de permissões ativas
 */
export function countActivePermissions(permissions) {
  if (!permissions || typeof permissions !== 'object') {
    return 0;
  }

  let count = 0;
  Object.keys(permissions).forEach(moduleKey => {
    const modulePermissions = permissions[moduleKey];
    if (modulePermissions && typeof modulePermissions === 'object') {
      Object.keys(modulePermissions).forEach(actionKey => {
        if (modulePermissions[actionKey] === true) {
          count++;
        }
      });
    }
  });

  return count;
}

/**
 * Verifica se um grupo tem permissões completas (tipo admin)
 * @param {Object} permissions - Objeto de permissões
 * @returns {Boolean} True se tiver acesso completo
 */
export function hasFullAccess(permissions) {
  if (!permissions || typeof permissions !== 'object') {
    return false;
  }

  const requiredModules = ['produtos', 'financeiro', 'vendas', 'usuarios'];
  
  for (const module of requiredModules) {
    if (!permissions[module]) return false;
    
    const modulePerms = permissions[module];
    // Verificar se tem pelo menos view e manage
    if (!modulePerms.view || (!modulePerms.manage && module !== 'relatorios')) {
      return false;
    }
  }

  return true;
}

